import {
  Activity,
  ArrowRightLeft,
  Check,
  ClipboardList,
  Droplets,
  HeartPulse,
  ListChecks,
  Pill,
  RefreshCw,
  Stethoscope,
  X,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { formatLocalizedTime } from '../i18n/localeFormat';
import {
  translateTaskItemLabel,
  translateTaskTitle,
} from '../i18n/displayLabels';
import {
  getTaskSourceKind,
  isMedicationTask,
  isPrnTask,
} from '../utils/carePlanTasks';
import { taskRequiresDocumentation } from '../utils/taskDocumentation';
import { formatFluidNetBadge } from '../utils/fluidBalanceBadge';
import { formatTaskNotGivenSubtitle } from '../utils/medicationDeviation';

function formatDueText(task, t, language) {
  if (task.status === 'complete') return { text: '', className: 'text-gray-500', urgency: 'done' };
  if (isPrnTask(task) && (!task.dueTime || task.status === 'ongoing')) {
    return { text: t('prnBadge'), className: 'text-gray-500', urgency: 'prn' };
  }
  if (task.status === 'overdue') {
    const windowEnd = new Date(task.windowEnd || task.dueTime || new Date().toISOString());
    const overdueMin = Math.max(1, Math.round((Date.now() - windowEnd.getTime()) / 60000));
    return { text: t('overdueBy', { minutes: overdueMin }), className: 'text-critical', urgency: 'overdue' };
  }
  if (task.status === 'ongoing' || task.status === 'in_progress' || !task.dueTime) {
    return { text: t('ongoing'), className: 'text-gray-500', urgency: 'upcoming' };
  }
  const due = new Date(task.dueTime);
  const diffMin = Math.round((due.getTime() - Date.now()) / 60000);
  if (diffMin <= 0) return { text: t('dueNow'), className: 'text-[#D97706]', urgency: 'due_now' };
  if (diffMin <= 30) {
    return {
      text: t('dueIn', { minutes: Math.max(0, diffMin) }),
      className: 'text-[#D97706]',
      urgency: 'due_now',
    };
  }
  const hhmm = formatLocalizedTime(due, language, { hour: '2-digit', minute: '2-digit', hour12: false });
  return { text: t('dueAt', { time: hhmm }), className: 'text-gray-500', urgency: 'upcoming' };
}

function getDescription(task, t) {
  if (isMedicationTask(task)) {
    return translateTaskItemLabel(t, task.itemLabel || task.title || t('medication'));
  }
  if (task.type === 'generated' || task.type === 'care_plan' || task.type === 'manual') {
    return translateTaskTitle(t, task.title);
  }
  if (task.itemLabel) {
    const group = translateTaskTitle(t, task.title);
    const item = translateTaskItemLabel(t, task.itemLabel);
    return item ? `${group}: ${item}` : group;
  }
  if (task.type === 'fluid_balance') return t('fluidBalance');
  return translateTaskTitle(t, task.title);
}

function getSourceLabel(task, t) {
  const kind = getTaskSourceKind(task);
  if (kind === 'voice') return t('taskSourceVoice');
  if (kind === 'care_plan') return t('taskSourceCarePlan');
  return t('taskSourceManual');
}

function getTaskIcon(task) {
  if (isMedicationTask(task)) return Pill;
  if (isPrnTask(task)) return ListChecks;
  if (task.type === 'fluid_balance') return Droplets;
  if (task.type === 'care_plan') return Stethoscope;
  if (task.sourceNoteType === 'vitals') return HeartPulse;
  if (task.sourceNoteType === 'progress_note') return ClipboardList;
  if (task.sourceNoteType === 'handover') return ArrowRightLeft;
  const title = String(task.title || task.itemLabel || '').toLowerCase();
  if (title.includes('pain') || title.includes('ألم')) return Activity;
  if (title.includes('fluid') || title.includes('drain')) return Droplets;
  return ClipboardList;
}

export default function TaskManagedCard({
  task,
  onToggleComplete,
  onOpenDocumentation,
  onOpenFluidBalance,
}) {
  const { t, language } = useLanguage();

  if (!task) return null;

  const completed = task.status === 'complete';
  const isNotGiven = completed && task.completionOutcome === 'not_given';
  const due = formatDueText(task, t, language);
  const description = getDescription(task, t);
  const sourceLabel = getSourceLabel(task, t);
  const needsDocumentation = taskRequiresDocumentation(task);
  const TaskIcon = getTaskIcon(task);
  const isFluidBalance = task.type === 'fluid_balance';
  const fluidNet =
    task.fluidSummary?.netBalance != null ? Number(task.fluidSummary.netBalance) : 0;
  const fluidBadge = isFluidBalance ? formatFluidNetBadge(t, fluidNet) : null;

  const completedTime = task.completedAt
    ? formatLocalizedTime(task.completedAt, language, {
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  const scheduledTime = task.dueTime
    ? formatLocalizedTime(task.dueTime, language, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    : null;

  const notGivenTitle = isNotGiven
    ? [
        description,
        task.dose || null,
        scheduledTime ? `— ${scheduledTime}` : null,
      ]
        .filter(Boolean)
        .join(' ')
    : null;

  const handleComplete = (e) => {
    e.stopPropagation();
    if (completed) {
      onToggleComplete?.(task.id);
      return;
    }
    if (needsDocumentation) {
      onOpenDocumentation?.(task);
      return;
    }
    onToggleComplete?.(task.id);
  };

  const handleCardActivate = () => {
    if (isFluidBalance) {
      onOpenFluidBalance?.(task);
    }
  };

  return (
    <div
      className={`border-b border-gray-100 bg-white px-1 py-3 last:border-b-0 ${
        completed && !isNotGiven ? 'opacity-65' : ''
      } ${isFluidBalance ? 'cursor-pointer' : ''}`}
      role={isFluidBalance ? 'button' : undefined}
      tabIndex={isFluidBalance ? 0 : undefined}
      onClick={isFluidBalance ? handleCardActivate : undefined}
      onKeyDown={
        isFluidBalance
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleCardActivate();
              }
            }
          : undefined
      }
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center">
          <TaskIcon
            className={`h-5 w-5 ${isNotGiven ? 'text-[#DC2626]' : 'text-gray-900'}`}
            strokeWidth={1.75}
          />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p
                className={`text-[15px] font-medium ${
                  isNotGiven
                    ? 'text-[#991B1B]'
                    : completed
                      ? 'text-gray-800 line-through'
                      : 'text-gray-800'
                }`}
              >
                {isNotGiven ? `✗ ${notGivenTitle}` : isFluidBalance ? fluidBadge : description}
              </p>
              {isNotGiven ? (
                <p className="mt-0.5 text-xs font-medium text-[#B91C1C]">
                  {formatTaskNotGivenSubtitle(t, task)}
                </p>
              ) : null}
              {isMedicationTask(task) && !completed && (task.dose || task.route) && (
                <p className="mt-0.5 text-xs text-gray-500">
                  {[task.dose, task.route].filter(Boolean).join(' · ')}
                </p>
              )}
              {task.isRecurring && !completed && (
                <span className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-gray-500">
                  <RefreshCw className="h-3 w-3 text-gray-900" strokeWidth={1.75} />
                  {t('recurringTask')}
                </span>
              )}
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <span className="inline-flex rounded-md bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
                  {sourceLabel}
                </span>
                {!completed && due.text ? (
                  <span className={`text-[11px] font-medium ${due.className}`}>
                    {isPrnTask(task) && (!task.dueTime || due.urgency === 'prn')
                      ? t('prnBadge')
                      : due.text}
                  </span>
                ) : null}
                {completed && !isNotGiven && completedTime ? (
                  <span className="text-[11px] text-gray-500">{completedTime}</span>
                ) : null}
              </div>
            </div>

            <button
              type="button"
              onClick={handleComplete}
              aria-label={
                isNotGiven
                  ? t('notGiven')
                  : completed
                    ? t('completedLabel')
                    : needsDocumentation
                      ? t('documentTask')
                      : t('markComplete')
              }
              className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-[4px] border bg-transparent ${
                isNotGiven ? 'border-[#DC2626]' : 'border-gray-900'
              }`}
            >
              {isNotGiven ? (
                <X className="h-4 w-4 text-[#DC2626]" strokeWidth={2.5} />
              ) : completed ? (
                <Check className="h-4 w-4 text-gray-900" strokeWidth={2.5} />
              ) : null}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
